import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Col, Row, Table } from 'react-bootstrap';
import { CartesianGrid, Area, AreaChart, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { getActivity, getActivityLaps, getActivitySamples } from '../api/trainer';
import { ActivityAdjustment } from '../api/types';
import AdjustmentModal from '../components/AdjustmentModal';
import ChartCard from '../components/ChartCard';
import PageHeader from '../components/PageHeader';
import StatusMessage from '../components/StatusMessage';
import { useAsync } from '../hooks/useAsync';
import {
  activityTypeLabel,
  formatDate,
  formatDistance,
  formatDuration,
  formatNumber,
  formatPace,
} from '../utils/format';

/** Stored samples from standing still have a huge pace (s/km); beyond this they are left out of the chart. */
const MAX_CHART_PACE = 1000;
const AXIS = { stroke: 'var(--text-muted)', fontSize: 12 };

const ActivityDetailPage: React.FC = () => {
  const { id } = useParams();
  const numericId = Number(id);
  const valid = Number.isInteger(numericId) && numericId > 0;

  const invalidId = () => Promise.reject(new Error('Invalid activity id'));
  const activity = useAsync(() => (valid ? getActivity(numericId) : invalidId()), [numericId]);
  const laps = useAsync(() => (valid ? getActivityLaps(numericId) : invalidId()), [numericId]);
  const samples = useAsync(() => (valid ? getActivitySamples(numericId) : invalidId()), [numericId]);

  const [showAdjustment, setShowAdjustment] = useState(false);
  // The API has no read endpoint for an adjustment, so this only holds what was saved in this visit.
  const [savedAdjustment, setSavedAdjustment] = useState<ActivityAdjustment | null>(null);

  if (!valid) {
    return <StatusMessage loading={false} error="That activity link is not valid." />;
  }

  const a = activity.data;
  const series = (samples.data ?? []).map(s => ({
    minute: Number((s.timeS / 60).toFixed(1)),
    pace: s.paceSPerKm != null && s.paceSPerKm <= MAX_CHART_PACE ? s.paceSPerKm : null,
    heartRate: s.hr,
    altitude: s.altitudeM,
  }));
  const hasSamples = series.length > 0;

  return (
    <>
      <PageHeader title={a?.name ?? 'Activity'}>
        <p>
          <Link to="/activities">← All activities</Link>
          {a && (
            <>
              {' · '}
              {activityTypeLabel(a.activityType)} · {formatDate(a.startTime)}
            </>
          )}
        </p>
      </PageHeader>

      <StatusMessage loading={activity.loading} error={activity.error} onRetry={activity.reload} />

      {a && (
        <>
          <Row className="g-3 mb-3">
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Distance</div>
                <div className="stat-value tabular">{formatDistance(a.distanceM)}</div>
              </section>
            </Col>
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Time</div>
                <div className="stat-value tabular">{formatDuration(a.durationS)}</div>
              </section>
            </Col>
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Avg pace</div>
                <div className="stat-value tabular">{formatPace(a.avgPaceSPerKm)}</div>
              </section>
            </Col>
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Heart rate</div>
                <div className="stat-value tabular">{formatNumber(a.avgHr)} bpm</div>
                <div className="stat-hint">max {formatNumber(a.maxHr)}</div>
              </section>
            </Col>
          </Row>

          <section className="surface mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <h2 className="mb-1">Official result</h2>
              {savedAdjustment ? (
                <p className="muted small mb-0">
                  Saved: {formatDistance(savedAdjustment.officialDistanceM)} in {formatDuration(savedAdjustment.officialTimeS)}
                  {savedAdjustment.note ? ` · ${savedAdjustment.note}` : ''}
                </p>
              ) : (
                <p className="muted small mb-0">
                  Use this when the run was tracked a bit long or short. The records then use your official time.
                </p>
              )}
            </div>
            <Button variant="outline-primary" onClick={() => setShowAdjustment(true)}>
              Set official time
            </Button>
          </section>
          <AdjustmentModal
            activityId={numericId}
            trackedDistanceM={a.distanceM}
            show={showAdjustment}
            onClose={() => setShowAdjustment(false)}
            onChanged={setSavedAdjustment}
          />

          {!a.fitDownloaded && (
            <Alert variant="info" className="py-2">
              Laps and the per-second charts come from the FIT file, which has not been downloaded for this activity yet.
            </Alert>
          )}

          <Row className="g-3 mb-3">
            <Col md={6}>
              <ChartCard title="Pace (min/km, lower is faster)" empty={!samples.loading && !hasSamples}>
                <LineChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="minute" {...AXIS} tickFormatter={v => `${v}m`} />
                  <YAxis {...AXIS} reversed width={48} domain={['dataMin - 10', 'dataMax + 10']} tickFormatter={v => formatDuration(Math.round(v))} />
                  <Tooltip />
                  <Line type="monotone" dataKey="pace" stroke="var(--chart-2)" dot={false} strokeWidth={2} connectNulls />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="Heart rate (bpm)" empty={!samples.loading && !hasSamples}>
                <LineChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="minute" {...AXIS} tickFormatter={v => `${v}m`} />
                  <YAxis {...AXIS} domain={['dataMin - 5', 'dataMax + 5']} />
                  <Tooltip />
                  <Line type="monotone" dataKey="heartRate" stroke="var(--chart-4)" dot={false} strokeWidth={2} />
                </LineChart>
              </ChartCard>
            </Col>
            <Col xs={12}>
              <ChartCard title="Elevation (m)" empty={!samples.loading && !hasSamples}>
                <AreaChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="minute" {...AXIS} tickFormatter={v => `${v}m`} />
                  <YAxis {...AXIS} />
                  <Tooltip />
                  <Area type="monotone" dataKey="altitude" stroke="var(--chart-1)" fill="var(--chart-1)" fillOpacity={0.2} />
                </AreaChart>
              </ChartCard>
            </Col>
          </Row>
        </>
      )}
      <StatusMessage loading={samples.loading} error={samples.error} onRetry={samples.reload} />

      <section className="surface">
        <h2>Laps</h2>
        <StatusMessage loading={laps.loading} error={laps.error} onRetry={laps.reload} />
        {laps.data && laps.data.length === 0 && <p className="muted mb-0">No laps recorded.</p>}
        {laps.data && laps.data.length > 0 && (
          <div className="table-responsive">
            <Table size="sm" className="trainer-table">
              <thead>
                <tr>
                  <th>Lap</th>
                  <th className="text-end">Distance</th>
                  <th className="text-end">Time</th>
                  <th className="text-end">Pace</th>
                  <th className="text-end">Avg HR</th>
                  <th className="text-end">Ascent</th>
                </tr>
              </thead>
              <tbody>
                {laps.data.map(lap => (
                  <tr key={lap.index}>
                    <td>{lap.index}</td>
                    <td className="text-end tabular">{formatDistance(lap.distanceM)}</td>
                    <td className="text-end tabular">{formatDuration(lap.durationS)}</td>
                    <td className="text-end tabular">{formatPace(lap.avgPaceSPerKm)}</td>
                    <td className="text-end tabular">{formatNumber(lap.avgHr)}</td>
                    <td className="text-end tabular">{formatNumber(lap.ascentM)} m</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </section>
    </>
  );
};

export default ActivityDetailPage;
